import { isEmptyObject } from "@/helpers/services/utils";
import { MARKETPLACE_APP_URL } from "@/helpers/variables/scopes";
import { i18n } from "@/plugins/vue-i18n";

const localAuthError = localStorage.getItem("auth_error") || false;

if (localAuthError) {
  localStorage.removeItem("auth_error");
}

const state = () => ({
  token: localStorage.getItem("token") || null,
  tokenReseller: localStorage.getItem("reseller") || null,
  isAuthenticating: false,
  showLogoutDialog: false,
  authError: localAuthError,
  isSimulation: false,
});

const getters = {
  isSimulation: (state) => state.isSimulation,
  tokenReseller: (state) =>
    state.tokenReseller ? "Bearer " + state.tokenReseller : null,
  isAuthenticated: (state, getters) => !isEmptyObject(getters.currentUser),
  hasVerifiedPhone: (state, { company }) => company && !!company.verified_phone,
  isSuspended: (state, { company }) => company && !!company.suspended,
  isAuthenticating: (state) => state.isAuthenticating,
  getRandomRetryMessages: () => () =>
    [
      [
        i18n.t("retry.waitingGoogle") +
          "</br>" +
          i18n.t("retry.ensureScopes", { link: MARKETPLACE_APP_URL }),
      ],
      [
        i18n.t("retry.justInstalled") +
          "</br>" +
          i18n.t("retry.ensureScopes", { link: MARKETPLACE_APP_URL }),
      ],
    ][Math.round(Math.random())],
  token: (state) => (state.token ? "Bearer " + state.token : null),
  showLogoutDialog: (state) => state.showLogoutDialog,
  authError: (state) => state.authError,
};

// actions
const actions = {
  async authentication({ dispatch, commit, getters }, token) {
    if (!token && !getters.token) return false;
    if (token) commit("setToken", token);

    if (getters.tokenReseller) {
      await dispatch("getCurrentReseller", { root: true });
    }

    try {
      const userPromise = dispatch("getCurrentUser", { root: true });

      // company and plans may ignore errors because the app may not be installed
      const companyPromise = !getters.company
        ? dispatch("getCompany", { root: true }).catch(() => null)
        : Promise.resolve();

      const plansPromise = dispatch("getContractedPlans").catch(() => null);

      await userPromise;
      await Promise.all([companyPromise, plansPromise]);

      const isAuthenticated = !isEmptyObject(getters.currentUser);

      return isAuthenticated;
    } catch (error) {
      console.error("Vuex: authentication()", error);
      return false;
    }
  },

  logoutWithDialog({ commit, dispatch }) {
    return new Promise((resolve) => {
      const MAX_TIME = 20000; //20s
      commit("setShowLogoutDialog", true);

      setTimeout(() => {
        dispatch("logout");
        resolve();
      }, MAX_TIME);
    });
  },

  logout() {
    const keysToBeKept = ["nextPage", "allow_trial", "auth_error"].reduce(
      (result, key) => [...result, [key, localStorage.getItem(key)]],
      []
    );
    localStorage.clear();
    for (const [key, value] of keysToBeKept) {
      if (value) {
        localStorage.setItem(key, value);
      }
    }
    window.location.href = "/login";
  },

  async login({ getters, commit, dispatch, rootState }, auth) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/auth/callback`;

    if (!rootState.hutk) {
      await dispatch("fetchHutk", null, { root: true });
    }
    const hutk = rootState.hutk;

    if (!auth.params) auth.params = {};
    auth.params.hutk = hutk;

    const payload = {
      callback_url: `${getters.appBaseUrl}/auth/callback`,
    };

    auth.params.allow_trial =
      localStorage.getItem("allow_trial") || "not_allowed";

    return this._vm.$axios
      .post(url, payload, auth)
      .then(({ data }) => {
        const user = data.user;
        const isGmail =
          user?.domain === "gmail.com" || user?.email?.includes("@gmail.com");
        const gmailBlocked = user?.is_gmail_enabled === false;

        if (isGmail && gmailBlocked) {
          commit("setAuthError", "INVALID_EMAIL_DOMAIN");
          return Promise.reject({
            code: "INVALID_EMAIL_DOMAIN",
          });
        }

        localStorage.setItem("companyOauth", data.company.oauth);
        commit("setToken", data.token);
        commit("setCurrentUser", data.user);
        commit("setCompany", data.company);
        commit("setTrialIsAllowed", data.trialIsAllowed);
        commit("setAuthError", false);
        dispatch("syncUsersAsync");
        dispatch("getContractedPlans");
        return Promise.resolve(data.token);
      })
      .catch(({ response }) => {
        if (response.data.code) {
          commit("setAuthError", response.data.code);
        } else {
          commit("setAuthError", "UNKNOWN_ERROR");
        }
        return Promise.reject(response.data);
      });
  },

  async retryLogin({ commit, dispatch, rootState }, auth) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/auth/authorizate`;

    if (auth.retry) {
      delete auth.retry;
    }

    if (!rootState.hutk) {
      await dispatch("fetchHutk", null, { root: true });
    }
    const hutk = rootState.hutk;

    if (!auth.params) auth.params = {};
    auth.params.hutk = hutk;

    return this._vm.$axios
      .get(url, auth)
      .then(({ data }) => {
        const user = data.user;
        const isGmail =
          user?.domain === "gmail.com" || user?.email?.includes("@gmail.com");
        const gmailBlocked = user?.is_gmail_enabled === false;

        if (isGmail && gmailBlocked) {
          commit("setAuthError", "INVALID_EMAIL_DOMAIN");
          return Promise.reject({
            code: "INVALID_EMAIL_DOMAIN",
          });
        }

        localStorage.setItem("companyOauth", data.company.oauth);
        commit("setToken", data.token);
        commit("setCurrentUser", data.user);
        commit("setCompany", data.company);
        commit("setTrialIsAllowed", data.trialIsAllowed);
        commit("setAuthError", false);
        dispatch("syncUsersAsync");
        dispatch("getContractedPlans");
        return Promise.resolve(data.token);
      })
      .catch(({ response }) => {
        if (response.data.code) {
          commit("setAuthError", response.data.code);
        } else {
          commit("setAuthError", "UNKNOWN_ERROR");
        }
        return Promise.reject(response.data);
      });
  },

  callLoginMicrosoft({ getters, commit }) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/auth/microsoft`;
    const payload = {
      callback_url: `${getters.appBaseUrl}/auth/callback/microsoft`,
    };

    return this._vm.$axios
      .post(url, payload)
      .then((response) => {
        window.location.href = response.data;
        return Promise.resolve();
      })
      .catch((error) => {
        console.error("callLoginMicrosoft(): ", error);
        const { code } = error?.response?.data || {};
        if (code) {
          commit("setAuthError", code);
        } else {
          commit("setAuthError", "UNKNOWN_ERROR");
        }
        return Promise.reject(code);
      });
  },
};

// mutations
const mutations = {
  setToken(state, token) {
    localStorage.setItem("token", token);
    state.token = token;
  },
  setTokenReseller(state, tokenReseller) {
    if (tokenReseller) {
      localStorage.setItem("reseller", tokenReseller);
    } else {
      localStorage.removeItem("reseller");
    }
    state.tokenReseller = tokenReseller;
  },
  setIsAuthenticating(state, isAuthenticating) {
    state.isAuthenticating = isAuthenticating;
  },
  setShowLogoutDialog(state, showLogoutDialog) {
    state.showLogoutDialog = showLogoutDialog;
  },
  setAuthError(state, error) {
    state.authError = error;
  },
  setIsSimulation(state, isSimulation) {
    state.isSimulation = isSimulation;
  },
};

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
